/**
 * API service layer — all endpoints wired to the real backend.
 *
 * ✅  loginUser              — POST /web/v1/auth/login
 * ✅  setPassword            — POST /web/v1/auth/change-password
 * ✅  sendOtp                — POST /web/v1/auth/send-otp
 * ✅  verifyOtp              — POST /web/v1/auth/verify-otp
 * ✅  forgotPassword         — delegates to sendOtp (POST /web/v1/auth/send-otp)
 * ✅  getUser                — GET  /web/v1/user/get/:userId
 * ✅  createUser             — POST /web/v1/user/create
 * ✅  listSenders            — GET  /web/v1/user/list?type=sender
 * ✅  listReceivers          — GET  /web/v1/user/list?type=receiver
 * ✅  getRequiredDocuments   — GET  /web/v1/kyc/required-documents
 * ✅  uploadKycFile          — POST /web/v1/kyc  (multipart)
 * ✅  getUserDocument        — GET  /web/v1/kyc/documents
 * ✅  getSupportedCurrency   — GET  /web/v1/supported-currency
 * ✅  createBank             — POST /web/v1/bank
 * ✅  getBanks               — GET  /web/v1/bank
 * ✅  getVaultBalance        — GET  /web/v1/vault/balance
 * ✅  getVaultLedger         — GET  /web/v1/vault/ledger
 * ✅  getInvoices            — GET  /web/v1/payment-requests
 * ✅  getSenderInvoices      — GET  /web/v1/payment-requests?toUserId=
 * ✅  getInvoiceById         — GET  /web/v1/payment-requests?id=
 * ✅  createInvoice          — POST /web/v1/payment-requests
 * ✅  uploadInvoiceDocument  — POST /web/v1/kyc/upload
 * ✅  createQuote            — POST /web/v1/quote
 * ✅  getTransactions        — GET  /web/v1/transactions
 * ✅  getTransactionById     — GET  /web/v1/transactions?transactionId=
 * ✅  createTransaction      — POST /web/v1/transactions
 * ✅  createWalletTransfer   — POST /web/v1/transactions/wallet-transfer
 *
 * refreshToken is handled transparently by the interceptor in utils/api.js.
 */

import axios from "axios";
import api from "../utils/api";

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://beta-brnx-api.borderless.world";

// ─── Auth ─────────────────────────────────────────────────────────────────────

/**
 * POST /web/v1/auth/login
 *
 * Unauthenticated — uses a plain axios call (no auth interceptor).
 * Returns the full API envelope: { status, message, data: { accessToken, refreshToken } }
 */
export const loginUser = async ({ email, password }) => {
  const res = await axios.post(
    `${BASE_URL}/web/v1/auth/login`,
    { email, password },
    {
      headers: { "Content-Type": "application/json" },
      timeout: 30_000,
    }
  );
  return res.data;
};

/**
 * POST /web/v1/auth/change-password
 *
 * Sets / changes the user's password. Called from the SetPassword screen
 * after a first-time invite login. Passes the invite token alongside the
 * new password — the backend must accept a token-based flow here.
 *
 * NOTE: If the backend requires oldPassword + OTP instead of a token, this
 * endpoint needs a companion route (e.g. /auth/set-password). Coordinate
 * with the backend team if this call returns a validation error.
 *
 * @param {string} email
 * @param {string} password   — new password
 * @param {string} [token]    — invite / temp token from navigation state
 */
export const setPassword = async ({ email, password, token }) => {
  const res = await api.post("/web/v1/auth/change-password", {
    username: email,
    newPassword: password,
    ...(token && { token }),
  });
  return res.data;
};

// ─── OTP ──────────────────────────────────────────────────────────────────────

/**
 * POST /web/v1/auth/send-otp
 *
 * Sends an OTP email to the given address.
 * @param {string} email
 * @param {string} [emailType="otp"]
 */
export const sendOtp = async ({ email, emailType = "otp" }) => {
  const res = await axios.post(
    `${BASE_URL}/web/v1/auth/send-otp`,
    { email, emailType },
    { headers: { "Content-Type": "application/json" }, timeout: 30_000 }
  );
  return res.data;
};

/**
 * POST /web/v1/auth/verify-otp
 *
 * Verifies an OTP for the given email.
 * @param {string} email
 * @param {string} hashID  — returned by send-otp
 * @param {string} otp     — 6-digit code entered by user
 */
export const verifyOtp = async ({ email, hashID, otp }) => {
  const res = await axios.post(
    `${BASE_URL}/web/v1/auth/verify-otp`,
    { email, hashID, otp },
    { headers: { "Content-Type": "application/json" }, timeout: 30_000 }
  );
  return res.data;
};

/**
 * Forgot-password flow — delegates to send-otp.
 * The page shows a success message regardless of the API result.
 */
// Kept for any legacy callers — now just a named alias.
export const forgotPassword = async ({ email }) => {
  return sendOtp({ email, emailType: "otp" });
};

/**
 * POST /web/v1/auth/forgot-password
 *
 * Resets the user's password using an OTP.
 * Must be preceded by sendOtp() to obtain the hashId.
 *
 * @param {string} newPassword
 * @param {string} username   — the user's email address
 * @param {string} hashId     — returned by sendOtp()
 * @param {string} otp        — 6-digit code entered by the user
 */
export const forgotPasswordSubmit = async ({ newPassword, username, hashId, otp }) => {
  const res = await axios.post(
    `${BASE_URL}/web/v1/auth/forgot-password`,
    { newPassword, username, hashId, otp },
    { headers: { "Content-Type": "application/json" }, timeout: 30_000 }
  );
  return res.data;
};

/**
 * POST /web/v1/auth/change-password  (authenticated)
 *
 * Changes the password for the currently logged-in user.
 * Must be preceded by sendOtp() to obtain the hashId.
 * On success, the caller should clear credentials and redirect to login.
 *
 * @param {string} username     — the user's email address
 * @param {string} oldPassword
 * @param {string} newPassword
 * @param {string} hashId       — returned by sendOtp()
 * @param {string} otp          — 6-digit code entered by the user
 */
export const changePassword = async ({ username, oldPassword, newPassword, hashId, otp }) => {
  const res = await api.post("/web/v1/auth/change-password", {
    username,
    oldPassword,
    newPassword,
    hashId,
    otp,
  });
  return res.data;
};

// ─── User ─────────────────────────────────────────────────────────────────────

/**
 * GET /web/v1/user/get/:userId
 *
 * Returns the full user record including `status` (PENDING | APPROVED | REJECTED).
 * Used by Login.jsx to route post-login and by the KYB flow to poll status.
 */
export const getUser = async (userId) => {
  const res = await api.get(`/web/v1/user/get/${userId}`);
  return res.data; // envelope: { status, message, data: { status, ... } }
};

/**
 * GET /web/v1/user/list?type=sender&page=&limit=
 *
 * Lists all senders linked to the logged-in receiver's partner.
 * The backend resolves the partnerId from the web user's JWT, so no userId param needed.
 * Returns: { data: UserDetailsResponse[], meta: { page, limit, total, totalPages } }
 */
export const listSenders = async (params = {}) => {
  const res = await api.get("/web/v1/user/list", {
    params: { type: "sender", ...params },
  });
  return res.data;
};

/**
 * GET /web/v1/user/list?type=receiver
 *
 * Lists all receivers linked to the logged-in sender's partner.
 * Used by the sender when selecting a receiver for a transaction.
 * Returns: { data: UserDetailsResponse[], meta: { page, limit, total, totalPages } }
 */
export const listReceivers = async (params = {}) => {
  const res = await api.get("/web/v1/user/list", {
    params: { type: "receiver", ...params },
  });
  return res.data;
};

/**
 * POST /web/v1/user/create
 *
 * Creates / invites a new user (sender or receiver).
 * @param {string} name
 * @param {string} email
 * @param {string} [phone]
 * @param {string} country  — ISO 3166-1 alpha-2
 * @param {string} type     — "sender" | "receiver"
 */
export const createUser = async ({ name, email, phone, country, type }) => {
  const res = await api.post("/web/v1/user/create", {
    name,
    email,
    phone,
    country,
    type,
    userName: email,
  });
  return res.data;
};

// ─── KYC ──────────────────────────────────────────────────────────────────────

/**
 * GET /web/v1/kyc/required-documents?transactionType=OFFRAMP&country=AE
 *
 * Returns the list of documents required for KYB for the given country and
 * transaction type. The `data` field is an array of docType strings
 * e.g. ["PASSPORT", "CERTIFICATE_OF_INCORPORATION"].
 */
export const getRequiredDocuments = async ({ transactionType, country }) => {
  const res = await api.get("/web/v1/kyc/required-documents", {
    params: { transactionType, country },
  });
  return res.data; // envelope: { data: [...], message: "Success" }
};

/**
 * POST /web/v1/kyc  (multipart/form-data)
 *
 * Uploads a KYC document. This single call handles both upload and submission —
 * there is no separate submit step.
 *
 * @param {File}   file             — the document file
 * @param {string} docType          — e.g. "PASSPORT" (as returned by required-documents)
 * @param {string} userId           — from the decoded JWT (Redux auth.userId)
 * @param {string} transactionType  — "OFFRAMP" | "ONRAMP"
 * @param {string} [side="front"]   — document side ("front" | "back")
 */
export const uploadKycFile = async ({
  file,
  docType,
  userId,
  transactionType,
  side = "front",
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("docType", docType.toLowerCase());
  formData.append("userId", userId);
  formData.append("uploadType", "manual");
  formData.append("side", side);
  formData.append("transactionType", transactionType);

  const res = await api.post("/web/v1/kyc", formData, {
    headers: { "Content-Type": undefined },
  });
  return res.data;
};

/**
 * GET /web/v1/kyc/documents?documentId=...&userId=...
 *
 * Retrieves a specific KYC document for a user.
 */
export const getUserDocument = async ({ documentId, userId }) => {
  const res = await api.get("/web/v1/kyc/documents", {
    params: { documentId, userId },
  });
  return res.data;
};

// ─── Currency ─────────────────────────────────────────────────────────────────

/**
 * GET /web/v1/supported-currency?country=NG&transactionType=OFFRAMP
 *
 * Returns the list of currencies supported for the given country and
 * transaction type.
 * @param {string} country          — ISO 3166-1 alpha-2
 * @param {string} transactionType  — "OFFRAMP" | "ONRAMP"
 */
export const getSupportedCurrency = async ({ country, transactionType }) => {
  const res = await api.get("/web/v1/supported-currency", {
    params: { country, transactionType },
  });
  return res.data;
};

// ─── Bank ─────────────────────────────────────────────────────────────────────

/**
 * POST /web/v1/bank
 *
 * Creates a bank account for the receiver.
 */
export const createBank = async ({
  accountNumber,
  accountHolderName,
  userId,
  routingNumber,
  thirdParty = "Bridge",
  paymentMethod,
  region,
  bankName,
  currency,
  streetLine,
  city,
  state,
  postalCode,
  country,
  isPrimary,
}) => {
  const res = await api.post("/web/v1/bank", {
    accountNumber,
    accountHolderName,
    userId,
    routingNumber,
    thirdParty,
    paymentMethod,
    region,
    bankName,
    currency,
    streetLine,
    city,
    state,
    postalCode,
    country,
    ...(isPrimary !== undefined && { isPrimary }),
  });
  return res.data;
};

/**
 * GET /web/v1/bank?userId=...
 *
 * Returns bank accounts for the given user.
 */
export const getBanks = async (userId) => {
  const res = await api.get("/web/v1/bank", { params: { userId } });
  return res.data;
};

// ─── Vault ────────────────────────────────────────────────────────────────────

/**
 * GET /web/v1/vault/balance?userId=:userId
 *
 * Returns the vault (USDC wallet) balance for the given user.
 * Response shape: { data: { userId, balance: [{ chain, symbol, name, balance }] } }
 */
export const getVaultBalance = async (userId) => {
  const res = await api.get("/web/v1/vault/balance", { params: { userId } });
  return res.data;
};

/**
 * GET /web/v1/vault/ledger?userId=:userId&page=&limit=&fromDate=&toDate=
 *
 * Returns the wallet ledger for the given user.
 * API response shape: { data: { page, limit, total, userId, vaultName, transactions: [] } }
 *
 * Normalised return shape:
 *   { data: LedgerEntry[], meta: { page, limit, total, totalPages } }
 */
export const getVaultLedger = async (userId, params = {}) => {
  const res = await api.get("/web/v1/vault/ledger", { params: { userId, ...params } });
  const payload = res.data?.data || {};

  const entries = Array.isArray(payload.transactions) ? payload.transactions : [];
  const total = payload.total ?? entries.length;
  const limit = payload.limit ?? params.limit ?? 20;

  return {
    data: entries,
    meta: {
      page: payload.page ?? 1,
      limit,
      total,
      totalPages: Math.ceil(total / limit) || 1,
    },
  };
};

// ─── Invoices (Payment Requests) ──────────────────────────────────────────────

/**
 * GET /web/v1/payment-requests?fromUserId=:userId
 *
 * Lists all invoices raised BY the logged-in receiver (fromUserId = receiver).
 * Optional filters: status, currency, page, limit.
 */
export const getInvoices = async (userId, params = {}) => {
  const res = await api.get("/web/v1/payment-requests", {
    params: { fromUserId: userId, ...params },
  });
  return res.data; // envelope: { meta: {...}, data: [...] }
};

/**
 * GET /web/v1/payment-requests?toUserId=:userId
 *
 * Lists all payment requests addressed TO the given sender (i.e. invoices
 * the sender needs to pay). Used on both the sender's invoice list and the
 * receiver's sender-detail page.
 * Optional filters: status, page, limit.
 * Returns: { meta: { page, limit, total, totalPages }, data: [...] }
 */
export const getSenderInvoices = async (userId, params = {}) => {
  const res = await api.get("/web/v1/payment-requests", {
    params: { toUserId: userId, ...params },
  });
  return res.data;
};

/**
 * GET /web/v1/payment-requests?id=:id
 *
 * Fetches a single payment request by UUID using the list endpoint.
 * Returns: { data: invoice } — first item from the data array.
 */
export const getInvoiceById = async (id) => {
  const res = await api.get("/web/v1/payment-requests", { params: { id } });
  const invoice = res.data?.data?.[0] || null;
  return { data: invoice };
};

/**
 * GET /web/v1/payment-requests/details?paymentRequestId=:id
 *
 * Fetches a single payment request by UUID using the list endpoint.
 * Returns: { data: invoice } — first item from the data array.
 */
//  Need to convert Everywher for now doing only for Invoice Detail Page
export const getInvoiceByPaymentRequestId = async (id) => {
  const res = await api.get("/web/v1/payment-requests/details", { params: { paymentRequestId: id } });
  const invoice = res.data?.data || null;
  return { data: invoice };
};


/**
 * GET /web/v1/payment-requests/details?paymentRequestId=:id
 *
 * Fetches the signed / public document URL for an invoice that has an
 * attached document. Call this on demand (when the user clicks "Invoice")
 * rather than on page load — then open the returned URL in a new tab.
 *
 * Returns: { data: { url | fileUrl | docUrl: "https://..." } }
 */
export const getInvoiceDetails = async (paymentRequestId) => {
  const res = await api.get("/web/v1/payment-requests/details", {
    params: { paymentRequestId },
  });
  return res.data;
};

/**
 * POST /web/v1/kyc/upload
 *
 * Uploads an invoice document to S3 and returns its URL.
 * Used before createInvoice to obtain the required docUrl.
 * Returns: { data: { fileUrl: "https://..." } }
 */
export const uploadInvoiceDocument = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  // Do NOT set Content-Type manually — setting it without a boundary breaks multipart parsing.
  // undefined clears the axios instance default so the browser sets the correct
  // "multipart/form-data; boundary=----..." header automatically.
  const res = await api.post("/web/v1/kyc/upload", formData, {
    headers: { "Content-Type": undefined },
  });
  return res.data;
};

/**
 * POST /web/v1/payment-requests
 *
 * Creates a new invoice (payment request).
 * @param {string} fromUserId  — receiver's userId (logged-in user who raises the invoice)
 * @param {string} toUserId    — sender's userId (the party who needs to pay)
 * @param {string} currency    — e.g. "USD"
 * @param {number} amount
 * @param {string} invoiceId   — reference number entered by the user (required by API)
 * @param {string} [dueDate]   — ISO date string YYYY-MM-DD (optional, omitted if blank)
 * @param {string} [docUrl]    — document URL from uploadInvoiceDocument (optional)
 * @param {string} [comment]   — free-text description / comment (optional)
 */
export const createInvoice = async ({
  fromUserId,
  toUserId,
  currency,
  amount,
  invoiceId,
  dueDate,
  docUrl,
  comment,
}) => {
  const res = await api.post("/web/v1/payment-requests", {
    fromUserId,
    toUserId,
    currency,
    amount,
    invoiceId,
    dueDate,
    ...(docUrl && { docUrl }),
    ...(comment && { comment }),
  });
  return res.data; // envelope: { data: { id, status, ... } }
};

// ─── Quote ────────────────────────────────────────────────────────────────────

/**
 * POST /web/v1/quote
 *
 * Creates and locks an exchange quote.
 * For invoice payments: fromCurrency = sender's local currency (e.g. "NGN"),
 * toCurrency = invoice currency (e.g. "USD"), amount = invoice amount (toCurrency units).
 *
 * @param {string} userId
 * @param {string} fromCurrency  — sender's local currency code (e.g. "NGN")
 * @param {string} toCurrency    — target currency code (e.g. "USD")
 * @param {string} amount        — amount in toCurrency units
 * @param {string} category      — "OFFRAMP" | "ONRAMP"
 *
 * Returns: { data: { id, fromCurrency, toCurrency, fromAmount, toAmount, rate,
 *                    networkFee, fxSpread, expiresAt, ... } }
 */
export const createQuote = async ({ userId, fromCurrency, toCurrency, amount, toAmount, category, paymentRequestId }) => {
  const body = { userId, fromCurrency, toCurrency, category };
  if (paymentRequestId) {
    body.paymentRequestId = paymentRequestId;
  }
  // toAmount takes precedence when provided (receiver-side fiat target);
  // fall back to amount (sender-side USDC input) when toAmount is absent.
  if (toAmount !== undefined && toAmount !== null && toAmount !== "") {
    body.toAmount = String(toAmount);
  } else {
    body.amount = String(amount);
  }
  const res = await api.post("/web/v1/quote", body);
  return res.data;
};

// ─── Transactions ─────────────────────────────────────────────────────────────

/**
 * GET /web/v1/transactions
 *
 * Lists transactions filtered by userId and optional params.
 * API response shape: { data: { page, limit, total, transactions: [...] } }
 * Normalised return shape: { data: [...], meta: { page, limit, total, totalPages } }
 */
export const getTransactions = async (params = {}) => {
  const res = await api.get("/web/v1/transactions", { params });
  const payload = res.data?.data || {};
  const transactions = payload.transactions || [];
  const total = payload.total || 0;
  const limit = payload.limit || params.limit || 20;
  return {
    data: transactions,
    meta: {
      page: payload.page || 1,
      limit,
      total,
      totalPages: Math.ceil(total / limit) || 1,
    },
  };
};

/**
 * GET /web/v1/transactions?transactionId=:transactionId
 *
 * Fetches a single transaction by its UUID using the web list endpoint with
 * transactionId param (triggers single-record mode in the service).
 *
 * Returns envelope: { data: { transactionId, txnType, status, quote,
 *                             bankDetails, fromDetails, toDetails, createdAt } }
 */


export const getTransactionById = async (id) => {
  const res = await api.get("/web/v1/transactions", { params: { transactionId: id } });
  // The transactions endpoint always returns a list envelope:
  // { data: { transactions: [...], total, page, limit } }
  // Extract the single matching record.
  const payload = res.data?.data;
  const txn = Array.isArray(payload?.transactions)
    ? payload.transactions[0] || null
    : payload || null;
  return { data: txn };
};

/**
 * POST /web/v1/transactions
 *
 * Initiates a transaction via the on-ramp / quote flow.
 * Used when the sender does NOT have sufficient USDC wallet balance.
 *
 * @param {string} quoteId            — locked quote ID from POST /web/v1/quote
 * @param {string} [paymentRequestId] — invoice ID (links the transaction to the invoice)
 * @param {string} [bankAccountId]    — optional sender bank account ID
 */
export const createTransaction = async ({ quoteId, paymentRequestId, bankAccountId } = {}) => {
  const res = await api.post("/web/v1/transactions", {
    quoteId,
    ...(paymentRequestId && { paymentRequestId }),
    ...(bankAccountId && { bankAccountId }),
  });
  return res.data;
};

/**
 * POST /web/v1/transactions/wallet-transfer
 *
 * Initiates a direct USDC wallet-to-wallet transfer.
 * Used when the sender has sufficient USDC balance to cover the invoice amount.
 *
 * @param {string} fromUserId — sender's userId
 * @param {string} toUserId   — receiver's userId (invoice.toUserId)
 * @param {number} amount     — invoice amount in USD (USDC)
 * @param {string} asset      — must be "USDC"
 */
export const createWalletTransfer = async ({ fromUserId, toUserId, amount, asset = "USDC" }) => {
  const res = await api.post("/web/v1/transactions/wallet-transfer", {
    fromUserId,
    toUserId,
    amount: Number(amount),
    asset,
  });
  return res.data;
};

// ─── Role Feature Mappings ────────────────────────────────────────────────────

/**
 * GET /web/v1/role-feature-mappings/user/:userId
 *
 * Returns the feature permissions for the logged-in user.
 * Used after login to derive quote flow permissions from the QUOTE feature's sub-features.
 */
export const getUserFeaturePermissions = async (userId) => {
  const res = await api.get(`/web/v1/role-feature-mappings/user/${userId}`);
  return res.data;
};

// --- OTC (Large-volume negotiated) requests ─────────────────────────────────────
/**
 * GET /web/v1/otc
 *
 * Lists OTC (large-volume negotiated) requests for the current sender.
 * Optional filters: status, page, limit.
 * Returns envelope: { data: { otcRequests: [...], meta: { total, page, limit, totalPages } } }
 */
export const getOtcRequests = async (params = {}) => {
  const res = await api.get("/web/v1/otc", { params });
  return res.data;
};

/**
 * GET /web/v1/otc/:otcRequestId
 *
 * Fetches a single OTC request by its UUID.
 * Returns envelope: { data: { otcRequestId, status, fromCurrency, toCurrency,
 *   requestedAmount, targetRate, otcFinalRate, payAmount, updatedAt } }
 */
export const getOtcRequestById = async (otcRequestId) => {
  const res = await api.get(`/web/v1/otc/${otcRequestId}`);
  return res.data;
};

/**
 * POST /web/v1/otc/:otcRequestId/respond
 *
 * Accepts or rejects a shared OTC quote.
 * @param {string} otcRequestId
 * @param {"ACCEPT"|"REJECT"} action
 * Returns envelope: { data: { paymentRequestId, expiresAt } } on ACCEPT.
 */
export const respondOtcRequest = async (otcRequestId, params) => {
  const res = await api.patch(`/web/v1/otc/${otcRequestId}/respond`, params);
  return res.data;
};

/**
 * POST /web/v1/otc
 *
 * Submits a new OTC (large-volume negotiated) rate request from the pay-invoice flow.
 * @param {string} quoteId
 * @param {string} paymentRequestId
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {number} requestedAmount
 * @param {number} [targetRate]
 * Returns envelope: { data: { otcRequestId, status, ... } }
 */
export const createOtcRequest = async (params) => {
  const res = await api.post("/web/v1/otc", params);
  return res.data;
};